import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Upload, Camera } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import LangSwitcher from "@/components/LangSwitcher";
import VMark from "@/components/vangard/VMark";
import Seo from "@/components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;
const STAGES = ["submitted", "interview", "board", "approved"];

function Field({ label, hint, children, testid }) {
  return (
    <label className="block" data-testid={testid ? `${testid}-wrap` : undefined}>
      <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-neutral-500 mt-1 inline-block">{hint}</span>}
    </label>
  );
}

const inputCls = "mt-3 w-full bg-transparent border-0 border-b border-white/15 focus:border-white/60 outline-none py-3 text-base font-light transition-colors placeholder:text-neutral-600";
const textareaCls = "mt-3 w-full bg-transparent border border-white/[0.12] rounded-2xl focus:border-white/45 outline-none py-3 px-4 text-base font-light transition-colors placeholder:text-neutral-600";

function Timeline({ status, history, t, navigate }) {
  const activeIndex = STAGES.indexOf(status);
  const declined = status === "declined";
  return (
    <div className="max-w-2xl mx-auto" data-testid="onboarding-timeline">
      <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.onb.eyebrow")}</div>
      <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">
        {declined ? <>{t("vc.onb.status.declined")}.</> : status === "approved" ? t("vc.onb.tl-approved-title") : t("vc.onb.tl-title")}
      </h1>
      <p className="mt-4 text-neutral-400 font-light leading-relaxed max-w-[52ch]">
        {declined ? t("vc.onb.tl-decl") : status === "approved" ? t("vc.onb.tl-approved-sub") : t("vc.onb.tl-sub")}
      </p>
      {!declined && (
        <ol className="mt-12 space-y-6" data-testid="onboarding-stages">
          {STAGES.map((s, i) => {
            const done = i < activeIndex || status === "approved";
            const current = i === activeIndex && status !== "approved";
            return (
              <li key={s} className="flex items-start gap-5" data-testid={`stage-${s}`}>
                <div className={`w-9 h-9 rounded-full border flex items-center justify-center shrink-0 ${done ? "bg-white text-black border-white" : current ? "border-white text-white" : "border-white/20 text-neutral-500"}`}>
                  {done ? <Check size={14} /> : <span className="font-serif text-sm">0{i + 1}</span>}
                </div>
                <div className="pt-1">
                  <div className={`font-serif text-lg ${current ? "text-white" : done ? "text-neutral-300" : "text-neutral-500"}`}>{t(`vc.onb.status.${s}`)}</div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {status === "approved" && (
        <button onClick={() => navigate("/concierge")} className="mt-12 inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors" data-testid="approved-open-concierge">
          {t("vc.onb.tl-open-concierge")} <ArrowRight size={14} />
        </button>
      )}
      {history && history.length > 0 && (
        <div className="mt-14">
          <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500 mb-4">{t("vc.onb.tl-history")}</div>
          <ul className="space-y-3 text-sm text-neutral-400 font-light">
            {history.slice().reverse().map((h, i) => (
              <li key={i} className="flex items-baseline gap-3">
                <span className="text-neutral-600 tabular-nums text-xs">{new Date(h.at).toLocaleDateString()}</span>
                <span className="text-white/90">{t(`vc.onb.status.${h.status}`)}</span>
                {h.note && <span className="text-neutral-500">— {h.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, loading, initialized, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [application, setApplication] = useState(null);
  const [loadingApp, setLoadingApp] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const [s1, setS1] = useState({ full_name: "", business_desc: "", age: "", photo: "", instagram: "", linkedin: "" });
  const [s2, setS2] = useState({ turnover: "", years_in_business: "", why_join: "", phone: "", telegram_id: "" });

  useEffect(() => {
    if (!initialized || loading) return;
    if (!user) { navigate("/login"); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/applications/me`, { withCredentials: true });
        if (cancelled) return;
        const app = data.application;
        setApplication(app);
        if (app) {
          setS1({
            full_name: app.full_name || user.name || "",
            business_desc: app.business_desc || "",
            age: app.age ?? "",
            photo: app.photo || "",
            instagram: app.instagram || "",
            linkedin: app.linkedin || "",
          });
          setS2({
            turnover: app.turnover || "",
            years_in_business: app.years_in_business ?? "",
            why_join: app.why_join || "",
            phone: app.phone || "",
            telegram_id: app.telegram_id || "",
          });
          if (app.status && app.status !== "draft") setStep(3);
          else if (app.turnover || app.phone) setStep(2);
        } else {
          setS1((v) => ({ ...v, full_name: user.name || "" }));
        }
      } catch (e) {
        toast.error(t("vc.onb.load-fail"));
      } finally {
        if (!cancelled) setLoadingApp(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialized, loading, user, navigate, t]);

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t("vc.onb.photo-too-big")); return; }
    const reader = new FileReader();
    reader.onload = () => setS1((v) => ({ ...v, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const submitStep1 = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/api/applications/step1`, {
        ...s1,
        age: parseInt(s1.age, 10) || 0,
      }, { withCredentials: true });
      toast.success(t("vc.onb.saved"));
      setStep(2);
    } catch (err) {
      toast.error(t("vc.onb.submit-fail"));
    } finally { setSaving(false); }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/api/applications/step2`, {
        ...s2,
        years_in_business: parseInt(s2.years_in_business, 10) || 0,
      }, { withCredentials: true });
      toast.success(t("vc.onb.submitted-toast"));
      const { data } = await axios.get(`${API}/api/applications/me`, { withCredentials: true });
      setApplication(data.application);
      if (refreshUser) refreshUser();
      setStep(3);
    } catch (err) {
      toast.error(t("vc.onb.submit-fail"));
    } finally { setSaving(false); }
  };

  if (!initialized || loading || loadingApp) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center" data-testid="onboarding-loading">
        <div className="w-10 h-10 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
      </div>
    );
  }

  const currentStatus = application?.status && application.status !== "draft" ? application.status : null;

  return (
    <div className="min-h-screen bg-[#050505] text-white marble-bg" data-testid="onboarding-page">
      <Seo title={t("vc.onb.title")} noindex />
      <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#050505]/75 border-b border-white/[0.06]">
        <div className="max-w-[1100px] mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-neutral-400 hover:text-white transition-colors" data-testid="onboarding-back" style={{ padding: "8px 0" }}>
            <ArrowLeft size={16} /> {t("vc.common.back")}
          </button>
          <div className="flex items-center gap-2.5">
            <img src="/logo-light.png" alt="" className="h-6 w-auto" aria-hidden="true" />
            <div className="font-serif text-lg">Vangard<span className="text-neutral-400">Club</span></div>
          </div>
          <LangSwitcher />
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 pt-28 sm:pt-32 pb-24 relative overflow-hidden">
        <VMark position="top-right" size={620} opacity={0.05} />

        {currentStatus ? (
          <Timeline status={currentStatus} history={application?.history} t={t} navigate={navigate} />
        ) : (
          <>
            <div className="flex items-center gap-4 text-neutral-400 mb-8">
              <span className="text-[11px] tracking-[0.35em] uppercase">{t("vc.onb.eyebrow")}</span>
              <span className="h-px w-10 bg-white/15" />
              <span className="text-[11px] tracking-[0.25em] uppercase">
                {t("vc.onb.step")} 0{step === 1 ? "2" : "3"} {t("vc.onb.of")} 05
              </span>
            </div>

            {step === 1 && (
              <motion.form key="s1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} onSubmit={submitStep1} className="max-w-xl space-y-10" data-testid="onboarding-step1">
                <div>
                  <h1 className="font-serif text-4xl sm:text-5xl leading-tight">
                    {t("vc.onb.s1-title-1")} <span className="italic text-neutral-300">{t("vc.onb.s1-italic")}</span>
                  </h1>
                  <p className="mt-4 text-neutral-400 font-light">{t("vc.onb.s1-sub")}</p>
                </div>

                <Field label={t("vc.onb.full-name")} testid="onb-name">
                  <input required maxLength={120} value={s1.full_name} onChange={(e) => setS1({ ...s1, full_name: e.target.value })} className={inputCls} data-testid="onb-name" />
                </Field>

                <Field label={t("vc.onb.business-desc")} hint={t("vc.onb.business-desc-hint")} testid="onb-biz">
                  <textarea required rows={3} maxLength={500} value={s1.business_desc} onChange={(e) => setS1({ ...s1, business_desc: e.target.value })} className={textareaCls} data-testid="onb-biz" />
                </Field>

                <div className="grid grid-cols-2 gap-6">
                  <Field label={t("vc.onb.age")} testid="onb-age">
                    <input required type="number" min="18" max="120" value={s1.age} onChange={(e) => setS1({ ...s1, age: e.target.value })} className={inputCls} data-testid="onb-age" />
                  </Field>
                  <Field label={t("vc.onb.photo")} hint={t("vc.onb.photo-hint")}>
                    <div className="mt-3 flex items-center gap-4">
                      {s1.photo && <img src={s1.photo} alt="" className="w-14 h-14 rounded-full object-cover grayscale border border-white/20" />}
                      <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={onPickPhoto} className="hidden" data-testid="onb-photo-input" />
                      <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 rounded-full text-[11px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/55 transition-colors" data-testid="onb-photo-btn">
                        {s1.photo ? <Camera size={13} /> : <Upload size={13} />}
                        {s1.photo ? t("vc.onb.change") : t("vc.onb.upload")}
                      </button>
                    </div>
                  </Field>
                </div>

                <div className="pt-2">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">
                    Social · <span className="text-neutral-600">{t("vc.onb.socials-hint")}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Instagram">
                      <input value={s1.instagram} onChange={(e) => setS1({ ...s1, instagram: e.target.value })} placeholder="@handle" className={inputCls} data-testid="onb-instagram" />
                    </Field>
                    <Field label="LinkedIn">
                      <input value={s1.linkedin} onChange={(e) => setS1({ ...s1, linkedin: e.target.value })} placeholder="https://linkedin.com/in/…" className={inputCls} data-testid="onb-linkedin" />
                    </Field>
                  </div>
                </div>

                <div className="pt-6">
                  <button type="submit" disabled={saving} className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 disabled:opacity-50 transition-colors" data-testid="onb-step1-submit">
                    {saving ? t("vc.onb.saving") : t("vc.onb.next")}
                    <ArrowRight size={14} />
                  </button>
                </div>
              </motion.form>
            )}

            {step === 2 && (
              <motion.form key="s2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} onSubmit={submitStep2} className="max-w-xl space-y-10" data-testid="onboarding-step2">
                <div>
                  <h1 className="font-serif text-4xl sm:text-5xl leading-tight">
                    {t("vc.onb.s2-title-1")} <span className="italic text-neutral-300">{t("vc.onb.s2-italic")}</span>
                  </h1>
                  <p className="mt-4 text-neutral-400 font-light">{t("vc.onb.s2-sub")}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label={t("vc.onb.turnover")} hint={t("vc.onb.turnover-hint")}>
                    <input required maxLength={40} value={s2.turnover} onChange={(e) => setS2({ ...s2, turnover: e.target.value })} className={inputCls} data-testid="onb-turnover" />
                  </Field>
                  <Field label={t("vc.onb.years")}>
                    <input required type="number" min="0" max="80" value={s2.years_in_business} onChange={(e) => setS2({ ...s2, years_in_business: e.target.value })} className={inputCls} data-testid="onb-years" />
                  </Field>
                </div>

                <Field label={t("vc.onb.why")} hint={t("vc.onb.why-hint")}>
                  <textarea required rows={5} maxLength={2000} value={s2.why_join} onChange={(e) => setS2({ ...s2, why_join: e.target.value })} className={textareaCls} data-testid="onb-why" />
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label={t("vc.onb.phone")}>
                    <input required value={s2.phone} onChange={(e) => setS2({ ...s2, phone: e.target.value })} placeholder="+380…" className={inputCls} data-testid="onb-phone" />
                  </Field>
                  <Field label={t("vc.onb.telegram")} hint={t("vc.onb.telegram-hint")}>
                    <input value={s2.telegram_id} onChange={(e) => setS2({ ...s2, telegram_id: e.target.value })} placeholder="@username" className={inputCls} data-testid="onb-telegram" />
                  </Field>
                </div>

                <div className="pt-6 flex items-center gap-4">
                  <button type="button" onClick={() => setStep(1)} className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors" data-testid="onb-step2-back">
                    <ArrowLeft size={14} /> {t("vc.onb.back")}
                  </button>
                  <button type="submit" disabled={saving} className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 disabled:opacity-50 transition-colors" data-testid="onb-step2-submit">
                    {saving ? t("vc.onb.saving") : t("vc.onb.submit")}
                    <ArrowRight size={14} />
                  </button>
                </div>
              </motion.form>
            )}
          </>
        )}
      </main>
    </div>
  );
}
